// Message action rule constants
export const CAN_EDIT_MESSAGES = 'can_edit_messages';
export const CAN_DELETE_MESSAGES = 'can_delete_messages';
export const CAN_HIDE_OWN_MESSAGES = 'can_hide_own_messages';
export const CAN_HIDE_OTHER_MESSAGES = 'can_hide_other_messages';
export const CAN_DELETE_OWN_MESSAGES = 'can_delete_own_messages';

/**
 * Check if any rule that requires toolbar is active
 * @param {Object} rules - The conversation rules object
 * @param {Object} message - The message data object
 * @returns {boolean} - True if toolbar should be shown
 */
export const shouldShowToolbar = (rules, message) => {
  if (!rules) return false;

  // Check if message can be edited
  const canEdit =
    (rules[CAN_EDIT_MESSAGES] && message.editing_allowed !== false) ||
    message.is_internal_note;

  // Check if message can be deleted
  const canDelete =
    rules[CAN_DELETE_MESSAGES] ||
    message.is_internal_note ||
    (message.is_sent_by_me && rules[CAN_DELETE_OWN_MESSAGES]);

  // Check if message can be hidden
  const canHide = message.is_sent_by_me
    ? rules[CAN_HIDE_OWN_MESSAGES]
    : rules[CAN_HIDE_OTHER_MESSAGES];

  return canEdit || canDelete || canHide;
};

/**
 * Get available actions for the message based on rules
 * @param {Object} rules - The conversation rules object
 * @param {Object} message - The message data object
 * @returns {Array} - Array of action objects with type, label, icon, enabled, and handler keys
 */
export const getAvailableActions = (rules, message) => {
  if (!rules) return [];

  const actions = [];

  // Edit action
  const canEdit =
    (rules[CAN_EDIT_MESSAGES] && message.editing_allowed !== false) ||
    message.is_internal_note;

  if (canEdit && message.is_sent_by_me) {
    actions.push({
      type: 'edit',
      labelKey: 'edit_comment',
      icon: 'fa-pen',
      enabled: true
    });
  }

  // Delete action
  const canDelete =
    rules[CAN_DELETE_MESSAGES] ||
    message.is_internal_note ||
    (message.is_sent_by_me && rules[CAN_DELETE_OWN_MESSAGES]);

  if (canDelete) {
    actions.push({
      type: 'delete',
      labelKey: 'delete_comment',
      icon: 'fa-trash',
      enabled: true
    });
  }

  // Hide action - check if user can hide their own or other messages
  const canHideOwn = message.is_sent_by_me && rules[CAN_HIDE_OWN_MESSAGES];
  const canHideOthers = !message.is_sent_by_me && rules[CAN_HIDE_OTHER_MESSAGES];

  if (canHideOwn || canHideOthers) {
    const isHidden = message.is_hidden_on_platform;
    actions.push({
      type: 'hide',
      labelKey: isHidden
        ? 'communication.messages.unhide_message'
        : 'communication.messages.hide_message',
      icon: isHidden ? 'fa-eye' : 'fa-eye-slash',
      enabled: true
    });
  }

  return actions;
};
